import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Truck, Leaf, Award, MapPin, ArrowRight, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
  {
    icon: Truck,
    title: "Livraison à domicile",
    description: "Nous livrons vos produits frais directement chez vous.",
  },
  {
    icon: Leaf,
    title: "Produits locaux",
    description: "Des produits issus de producteurs normands de confiance.",
  },
  {
    icon: Award,
    title: "Qualité garantie",
    description: "Une sélection rigoureuse pour vous offrir le meilleur.",
  },
];

const categories = [
  {
    title: "Viandes",
    description: "Bœuf, veau, agneau, porc, volailles",
    image: "https://images.unsplash.com/photo-1603048297172-c92544798d5a?w=600",
    link: "/viandes",
  },
  {
    title: "Charcuterie",
    description: "Saucissons, jambons, pâtés",
    image: "https://images.pexels.com/photos/5491279/pexels-photo-5491279.jpeg?auto=compress&cs=tinysrgb&w=600",
    link: "/charcuterie",
  },
  {
    title: "Fruits & Légumes",
    description: "Frais et de saison",
    image: "https://images.unsplash.com/photo-1540420773420-3366772f4999?w=600",
    link: "/fruits-legumes",
  },
  {
    title: "Crèmerie",
    description: "Fromages, œufs et produits laitiers",
    image: "https://images.unsplash.com/photo-1452195100486-9cc805987862?w=600",
    link: "/cremerie",
  },
  {
    title: "Conserves",
    description: "Plats cuisinés et légumes",
    image: "https://images.pexels.com/photos/35567499/pexels-photo-35567499.jpeg?auto=compress&cs=tinysrgb&w=600",
    link: "/conserves",
  },
  {
    title: "Desserts",
    description: "Tartes, mousses et douceurs",
    image: "https://images.unsplash.com/photo-1568571780765-9276ac8b75a2?w=600",
    link: "/desserts",
  },
];

export default function Home() {
  const [content, setContent] = useState({});
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, promosRes] = await Promise.all([
          axios.get(`${API}/content`),
          axios.get(`${API}/promotions?active_only=true`),
        ]);
        
        const contentMap = {};
        contentRes.data.forEach((item) => {
          contentMap[item.key] = item.value;
        });
        setContent(contentMap);
        setPromotions(promosRes.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight">
              {content.hero_title || "Viande et légumes livrés chez vous"}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed">
              {content.hero_subtitle || "Des produits frais et locaux, directement de nos producteurs normands à votre table."}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="btn-primary" data-testid="discover-products-btn">
                <Link to="/viandes">
                  Découvrir nos produits
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" data-testid="contact-us-btn">
                <Link to="/contact">Nous contacter</Link>
              </Button>
            </div>
            
            {/* Delivery Zone Badge */}
            <div className="mt-8 inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full">
              <MapPin className="h-5 w-5" />
              <span className="font-medium">
                {content.delivery_zone || "Livraison dans un rayon de 50 km autour de Pont-Audemer"}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="text-center p-6"
                data-testid={`feature-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 text-primary rounded-full mb-4">
                  <feature.icon className="h-8 w-8" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24 bg-muted/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">Nos produits</h2>
            <p className="section-subtitle">
              Découvrez notre sélection de produits frais et locaux
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <Link
                key={index}
                to={category.link}
                className="group"
                data-testid={`category-${index}`}
              >
                <Card className="overflow-hidden card-hover h-full">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-heading text-lg font-semibold text-gray-900 group-hover:text-primary">
                      {category.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Promotions Section */}
      {promotions.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="section-title flex items-center gap-3">
                  <Tag className="h-8 w-8 text-accent" />
                  Promotions en cours
                </h2>
                <p className="section-subtitle">
                  Profitez de nos offres spéciales
                </p>
              </div>
              <Button asChild variant="outline" data-testid="see-all-promos-btn">
                <Link to="/promotions">Voir toutes</Link>
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {promotions.map((promo) => (
                <Card key={promo.id} className="overflow-hidden border-accent/20" data-testid={`promo-${promo.id}`}>
                  {promo.image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={promo.image_url}
                        alt={promo.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-heading text-xl font-semibold text-gray-900">
                          {promo.title}
                        </h3>
                        <p className="text-gray-600 mt-2">{promo.description}</p>
                      </div>
                      {promo.discount && (
                        <span className="bg-accent text-white text-sm font-bold px-3 py-1 rounded">
                          {promo.discount}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About Section */}
      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">
              À propos de nous
            </h2>
            <p className="text-lg text-white/90 leading-relaxed">
              {content.about_text || "Nous sommes une entreprise familiale passionnée par les produits du terroir normand. Depuis plus de 10 ans, nous sélectionnons avec soin les meilleures viandes et les légumes les plus frais pour vous les livrer directement à domicile."}
            </p>
            <Button
              asChild
              variant="secondary"
              size="lg"
              className="mt-8 bg-white text-primary hover:bg-white/90"
              data-testid="contact-cta-btn"
            >
              <Link to="/contact">Demander à être rappelé</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
